import { useState, useCallback } from 'react';

const FOLDER_NAME = 'Idiawisdom Backups';

export const useGoogleDrive = () => {
  const [isBackupLoading, setIsBackupLoading] = useState(false);
  const [backupLogs, setBackupLogs] = useState([]);
  const [driveBackups, setDriveBackups] = useState([]);

  const addLog = useCallback((message) => {
    setBackupLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  }, []);

  // 1. Find or create the backups folder
  const findOrCreateFolder = useCallback(async (token) => {
    addLog('Searching for backup folder in Google Drive...');
    
    // Query for folder
    const query = `name = '${FOLDER_NAME}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
    const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id, name)`;
    const searchRes = await fetch(searchUrl, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!searchRes.ok) {
      const errText = await searchRes.text();
      console.error('Google Drive search failed:', searchRes.status, errText);
      if (searchRes.status === 401) {
        throw new Error('UNAUTHORIZED_DRIVE_SESSION');
      }
      throw new Error(`Failed to search Google Drive folder (HTTP ${searchRes.status}).`);
    }

    const searchData = await searchRes.json();
    if (searchData.files && searchData.files.length > 0) {
      const folderId = searchData.files[0].id;
      addLog(`Found existing folder: '${FOLDER_NAME}' (ID: ${folderId})`);
      return folderId;
    }

    // Not found, create a new folder
    addLog(`Folder '${FOLDER_NAME}' not found. Creating a new one...`);
    const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder'
      })
    });

    if (!createRes.ok) {
      throw new Error('Failed to create backup folder in Google Drive.');
    }

    const createData = await createRes.json();
    addLog(`Created folder successfully! (ID: ${createData.id})`);
    return createData.id;
  }, [addLog]);

  // 2. Fetch all backups in the folder
  const fetchBackupsList = useCallback(async (token, folderId) => {
    try {
      const query = `'${folderId}' in parents and trashed = false`;
      const listUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&orderBy=createdTime desc&fields=files(id, name, createdTime, webViewLink, size)`;
      const res = await fetch(listUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDriveBackups(data.files || []);
      }
    } catch (err) {
      console.error('Error listing backups:', err);
    }
  }, []);

  // 3. Upload backup file
  const performBackup = useCallback(async (token, dbData) => {
    setIsBackupLoading(true);
    setBackupLogs([]);
    addLog('Starting backup protocol...');

    try {
      if (!token) {
        throw new Error('NO_TOKEN_AVAILABLE');
      }

      // Step A: Find or Create folder
      const folderId = await findOrCreateFolder(token);

      // Step B: Bundle data and prepare file name
      addLog('Packaging Firestore database collections...');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `idiawisdom_backup_${timestamp}.json`;
      
      const fileData = JSON.stringify({
        meta: {
          app: 'idiawisdom.com',
          timestamp: new Date().toISOString(),
          version: '1.0.0',
          recordsCount: {
            posts: dbData.posts?.length || 0,
            quotes: dbData.quotes?.length || 0,
            poems: dbData.poems?.length || 0,
            categories: dbData.categories?.length || 0
          }
        },
        payload: {
          posts: dbData.posts || [],
          quotes: dbData.quotes || [],
          poems: dbData.poems || [],
          categories: dbData.categories || []
        }
      }, null, 2);

      // Step C: Multipart Upload
      addLog(`Uploading package: '${fileName}'...`);
      const metadata = {
        name: fileName,
        parents: [folderId],
        mimeType: 'application/json'
      };

      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
      const fileBlob = new Blob([fileData], { type: 'application/json' });

      const formData = new FormData();
      formData.append('metadata', metadataBlob);
      formData.append('file', fileBlob);

      const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink';
      const uploadRes = await fetch(uploadUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!uploadRes.ok) {
        throw new Error('Failed to upload backup package to Google Drive.');
      }

      const uploadData = await uploadRes.json();
      addLog(`Backup uploaded successfully! ID: ${uploadData.id}`);
      addLog('Refreshing backup archive database logs...');
      
      // Refresh list
      await fetchBackupsList(token, folderId);

      return {
        success: true,
        file: uploadData
      };

    } catch (error) {
      addLog(`❌ Error: ${error.message}`);
      return {
        success: false,
        error: error.message
      };
    } finally {
      setIsBackupLoading(false);
    }
  }, [addLog, findOrCreateFolder, fetchBackupsList]);

  // 4. Download file content from Google Drive
  const downloadBackupContent = useCallback(async (token, fileId) => {
    addLog(`Initiating download of package (ID: ${fileId})...`);
    try {
      const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
      const res = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to download package content (HTTP ${res.status}).`);
      }
      
      const payload = await res.json();
      addLog("Package payload successfully retrieved & decoded.");
      return { success: true, data: payload };
    } catch (error) {
      addLog(`❌ Download Error: ${error.message}`);
      return { success: false, error: error.message };
    }
  }, [addLog]);

  return {
    isBackupLoading,
    backupLogs,
    driveBackups,
    performBackup,
    fetchBackupsList,
    findOrCreateFolder,
    downloadBackupContent
  };
};
